import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TableStructure from "../../CommonComponents/TableStructure";
import { useToast } from '@chakra-ui/react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Text,
  Progress,
  Box,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { getFileStats, deleteFileByFileName } from "../../services/FileList";
import { showErrorToast, showSuccessToast } from "../../util/errorHandler";

const FileList = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortField, setSortField] = useState("updatedAt");
  const [searchText, setSearchText] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const session = useSelector((state) => state.session);
  const isInitialLoad = useRef(true);
  const toast = useToast();
  const cancelRef = React.useRef();
  const progressIntervalRef = useRef(null);
  const limit = 50; // Number of records per page
  
  // Fetch files from the API
  const fetchFiles = async (page, term) => {
    setLoading(true);
    try {
      const response = await getFileStats(limit, page, term);
      
      if (response?.data?.data) {
        const fileData = response.data.data.map(file => ({
          id: file.file_name, // Using file_name as ID since it should be unique
          fileName: file.file_name,
          recordCount: file.count,
          document_types: file.document_types,
          classification: file.classification,
          updatedAt: file.updatedAt ? new Date(file.updatedAt) : new Date(0)
        }));
        
        // Sort by updatedAt in descending order by default
        const sortedData = sortData(fileData, sortField, sortOrder);
        setRecords(sortedData);
        setTotalPages(Math.ceil(response?.data?.total / limit) || 1);
        setTotalRecords(response?.data?.total);
      } else {
        setRecords([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching files:', error);
            showErrorToast(toast, "Failed to fetch file list");
      setLoading(false);
      setRecords([]);
    }
  };

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false; // Skip the first render
      setCurrentPage(1); // Reset to page 1 for a new search
      fetchFiles(1, searchTerm);
      setSearchText(searchTerm);
    }
  };

  // Handle clear search
  const handleClear = () => {
    fetchFiles(1, "");
    setSearchText('');
  };

  // Sort data helper function
  const sortData = (data, field, order) => {
    return [...data].sort((a, b) => {
      if (field === 'updatedAt') {
        // For dates, we need to compare timestamps
        const dateA = a[field] instanceof Date ? a[field] : new Date(a[field]);
        const dateB = b[field] instanceof Date ? b[field] : new Date(b[field]);
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (field === 'recordCount') {
        // For numbers
        return order === 'asc' ? a[field] - b[field] : b[field] - a[field];
      } else {
        // For strings
        return order === 'asc' 
          ? a[field]?.toString().localeCompare(b[field]?.toString())
          : b[field]?.toString().localeCompare(a[field]?.toString());
      }
    });
  };

  // Handle column header click for sorting
  const handleSort = (field) => {
    const newOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    
    const sorted = sortData(records, field, newOrder);
    setRecords(sorted);
  };

  // Legacy sort function (kept for compatibility with TableStructure)
  const sortRecords = () => {
    handleSort('updatedAt');
  };

  useEffect(() => {
    fetchFiles(currentPage, searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  // Format date function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return 'N/A';
      
      return dateObj.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Define table columns
  const columns = [
    {
      accessor: 'fileName',
      label: 'File Name',
      sortable: true,
      onSort: () => handleSort('fileName')
    },
    {
      accessor: 'recordCount',
      label: 'Record Count',
      render: (value) => value.toLocaleString(),
      sortable: true,
      onSort: () => handleSort('recordCount')
    },
    {
      accessor: 'document_types',
      label: 'Document Types'
    },
    {
      accessor: 'classification',
      label: 'Classification'
    },
    {
      accessor: 'updatedAt',
      label: 'Last Updated',
      render: (value) => formatDate(value),
      sortable: true,
      onSort: () => handleSort('updatedAt')
    },
    {
      accessor: 'actions',
      label: 'Actions',
      render: (_, record) => (
        <Button
          colorScheme="red"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            openDeleteDialog(record);
          }}
        >
          Delete
        </Button>
      )
    }
  ];

  // Delete functionality
  const openDeleteDialog = (file) => {
    setFileToDelete(file);
    setIsDeleteDialogOpen(true);
    setDeleteProgress(0);
  };

  const startProgressSimulation = () => {
    // Simulate progress for large files
    setDeleteProgress(0);
    let progress = 0;
    
    progressIntervalRef.current = setInterval(() => {
      // Increment progress but slow down as it approaches 90%
      // This creates a more realistic simulation where the process slows down near completion
      if (progress < 90) {
        const increment = Math.max(1, 10 - Math.floor(progress / 10));
        progress += increment;
        setDeleteProgress(progress);
      }
    }, 300);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      startProgressSimulation();
      
      await deleteFileByFileName(fileToDelete.fileName);
      
      // Clear the interval and set progress to 100% when deletion is complete
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setDeleteProgress(100);
      
      // Short delay to show 100% completion before closing
      setTimeout(() => {
        setIsDeleting(false);
        setIsDeleteDialogOpen(false);
        setFileToDelete(null);
        
        showSuccessToast(toast, `Records for file "${fileToDelete.fileName}" deleted successfully`);
        
        // Refresh the current page
        fetchFiles(currentPage, searchText);
      }, 500);
      
    } catch (error) {
      console.error('Error deleting file:', error);
      
      // Clear the interval on error
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      setIsDeleting(false);
      setDeleteProgress(0);
      
            showErrorToast(toast, "Failed to delete records");
    }
  };

  const closeDeleteDialog = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
      setFileToDelete(null);
      setDeleteProgress(0);
    }
  };

  return (
    <>
      <TableStructure
        isAuthenticated={session?.isAuthenticated}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        totalRecords={totalRecords}
        type="file-list"
        title="File List"
        columns={columns}
        pagination={true}
        disableRowClick={true}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDeleteDialog}
        closeOnOverlayClick={!isDeleting}
        closeOnEsc={!isDeleting}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isDeleting ? 'Deleting Records...' : 'Delete Records'}
            </AlertDialogHeader>

            <AlertDialogBody>
              {isDeleting ? (
                <Box>
                  <Text mb={3}>Deleting records for file:</Text>
                  <Text fontWeight="bold" mb={4}>{fileToDelete?.fileName}</Text>
                  <Progress 
                    value={deleteProgress} 
                    size="md" 
                    colorScheme="red" 
                    hasStripe
                    isAnimated
                    mb={3}
                  />
                  <Flex justify="center" mt={2}>
                    <Spinner size="sm" mr={2} />
                    <Text>Please wait, this may take a while...</Text>
                  </Flex>
                </Box>
              ) : (
                <>
                  Are you sure you want to delete the records for this file?
                  <Text mt={2} fontWeight="bold">{fileToDelete?.fileName}</Text>
                </>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              {!isDeleting && (
                <>
                  <Button ref={cancelRef} onClick={closeDeleteDialog}>
                    Cancel
                  </Button>
                  <Button colorScheme="red" onClick={handleDelete} ml={3}>
                    Delete
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default FileList; 